/* eslint-disable @typescript-eslint/no-unused-vars */
import { workspaceApi } from "@/api/workspace-api";
import { Card } from "@/components/ui/card";
import { cn, TASKS } from "@/lib/utils";
import { useWorkspaces } from "@/store/workspace-store";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle,
  FileText,
  Globe,
  Info,
  Lock,
  PlusCircle,
  Trophy,
  Trophy as TrophyIcon,
  Users,
} from "lucide-react";
import { useWorkspaceNavigation } from "@/hooks/use-workspace-navigation";
const iconMap = {
  PlusCircle,
  File: FileText,
  Globe,
  Info,
  Users,
  Trophy,
};

interface OpportunityTask {
  name: string;
  completed: boolean;
  credits: number;
  completed_on?: Date;
}

interface TasksResponse {
  status: string;
  tasks: OpportunityTask[];
  message?: string;
}

export default function GettingStartedComponent() {
  const { activeWorkspaceId } = useWorkspaces();
  const { navigate } = useWorkspaceNavigation();
  const {
    data: tasksData,
    isLoading,
    error,
  } = useQuery<TasksResponse>({
    queryKey: ["tasks", activeWorkspaceId],
    queryFn: () => workspaceApi.fetchOpportunityTasks(activeWorkspaceId ?? ""),
  });

  const completedTasks =
    tasksData?.tasks
      ?.filter((task) => task.completed)
      .map((task) => task.name) ?? [];

  const totalTasks = Object.keys(TASKS).filter(
    (key) => !TASKS[key].optional
  ).length;

  // Count only completed mandatory tasks
  const completedMandatoryCount = completedTasks.filter(
    (taskName) => !TASKS[taskName]?.optional
  ).length;

  const completionPercentage = Math.min(
    Math.round((completedMandatoryCount / totalTasks) * 100),
    100
  );

  // Separate mandatory and optional tasks
  const mandatoryTasks = Object.entries(TASKS).filter(
    ([_, task]) => !task.optional
  );
  const optionalTasks = Object.entries(TASKS).filter(
    ([_, task]) => task.optional
  );

  // Check if all mandatory tasks are completed
  const allMandatoryCompleted = mandatoryTasks.every(([key]) =>
    completedTasks.includes(key)
  );

  // Function to determine the current active task
  const getCurrentTask = () => {
    for (const [key] of mandatoryTasks) {
      if (!completedTasks.includes(key)) {
        return key;
      }
    }
    return null;
  };

  const currentTask = getCurrentTask();

  const handleTaskClick = (taskKey: string) => {
    const task = TASKS[taskKey];
    navigate(`${task.url}`);
  };

  if (isLoading) {
    return <div>Loading tasks...</div>; // Or your preferred loading component
  }

  if (error) {
    return <div>Error loading tasks</div>; // Or your preferred error component
  }

  return (
    <div className="container mx-auto max-w-4xl">
      <div className="text-center mb-12">
        <h1 className="text-5xl font-bold mb-4">Your Due Diligence Journey</h1>
        <p className="text-muted-foreground text-xl">
          Complete each step to unlock the next level
        </p>

        {/* Progress Overview Card */}
        <Card className="mt-5 max-w-md mx-auto p-4 bg-gradient-to-r from-primary/5 to-primary/10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrophyIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">Progress Overview</h2>
            </div>
            <span className="text-xl font-bold text-primary">
              {completionPercentage}%
            </span>
          </div>
          {/* Progress Bar */}
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            {completedMandatoryCount} of {totalTasks} required tasks completed
          </div>
        </Card>
      </div>

      <div className="relative flex flex-col space-y-4">
        {/* Show mandatory tasks first */}
        {mandatoryTasks.map(([key, task]) => {
          const IconComponent = iconMap[task.icon as keyof typeof iconMap];
          const isCompleted = completedTasks.includes(key);
          const isActive = key === currentTask;
          const isLocked = !isCompleted && key !== currentTask;

          return (
            <div key={key} className="relative group">
              <Card
                className={cn(
                  "p-4 transition-all duration-300",
                  isLocked
                    ? "opacity-50"
                    : "hover:shadow-xl hover:scale-[1.02]",
                  isActive && "ring-2 ring-primary ring-offset-4",
                  isCompleted && "bg-primary/5"
                )}
              >
                <div className="flex items-start gap-6">
                  {/* Status Circle */}
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center relative z-10 transition-colors duration-300",
                      isCompleted
                        ? "bg-primary text-primary-foreground"
                        : isActive
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground group-hover:bg-primary/5"
                    )}
                  >
                    {isLocked ? (
                      <Lock className="w-5 h-5" />
                    ) : isCompleted ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      IconComponent && <IconComponent className="w-5 h-5" />
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          {task.displayName}
                          {task.optional && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/10 text-muted-foreground font-normal">
                              Optional
                            </span>
                          )}
                        </h3>
                        <p className="text-muted-foreground text-sm mt-1">
                          {task.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!task.optional && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            Step {task.order} of {totalTasks}
                          </span>
                        )}
                      </div>
                    </div>

                    {isActive && !isCompleted && (
                      <button
                        className="mt-3 bg-primary text-primary-foreground px-4 py-1.5 rounded-md hover:bg-primary/90 transition-colors font-medium text-sm"
                        onClick={() => handleTaskClick(key)}
                      >
                        Start This Step →
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          );
        })}

        {/* Show optional tasks section only after completing all mandatory tasks */}
        {allMandatoryCompleted && optionalTasks.length > 0 && (
          <>
            <div className="mt-12 mb-6 text-center">
              <h2 className="text-xl font-semibold">Optional Tasks</h2>
              <p className="text-muted-foreground mt-1 text-sm">
                Unlock additional deals by completing these optional tasks
              </p>
            </div>

            {optionalTasks.map(([key, task]) => {
              const IconComponent = iconMap[task.icon as keyof typeof iconMap];
              const isCompleted = completedTasks.includes(key);
              const isActive = !isCompleted && allMandatoryCompleted;

              return (
                <div key={key} className="relative group">
                  <Card
                    className={cn(
                      "p-4 transition-all duration-300",
                      "hover:shadow-xl hover:scale-[1.02]",
                      isActive && "ring-2 ring-primary ring-offset-4",
                      isCompleted && "bg-primary/5"
                    )}
                  >
                    <div className="flex items-start gap-6">
                      {/* Status Circle */}
                      <div
                        className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center relative z-10 transition-colors duration-300",
                          isCompleted
                            ? "bg-primary text-primary-foreground"
                            : isActive
                            ? "bg-primary/10 text-primary"
                            : "bg-muted text-muted-foreground group-hover:bg-primary/5"
                        )}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          IconComponent && <IconComponent className="w-5 h-5" />
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                              {task.displayName}
                              {task.optional && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/10 text-muted-foreground font-normal">
                                  Optional
                                </span>
                              )}
                            </h3>
                            <p className="text-muted-foreground text-sm mt-1">
                              {task.description}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0"></div>
                        </div>

                        {isActive && !isCompleted && (
                          <button
                            className="mt-3 bg-primary text-primary-foreground px-4 py-1.5 rounded-md hover:bg-primary/90 transition-colors font-medium text-sm"
                            onClick={() => handleTaskClick(key)}
                          >
                            Start This Step →
                          </button>
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className="mt-12 text-center">
        <div className="inline-flex items-center justify-center gap-6 text-muted-foreground bg-muted/20 px-4 py-2 rounded-full text-sm">
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-primary" />
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-primary/10" />
            <span>Current</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Lock className="w-4 h-4" />
            <span>Locked</span>
          </div>
        </div>
      </div>
    </div>
  );
}
